import type * as React from "react";
import { type FC, useRef, useState } from "react";
import Modal, { ModalSize } from "../../common/components/Modal";
import { Link } from "../../common/components/Link";
import { sendLoginRequest } from "../api/DoLogin";
import { doOnLoginAction, isForcedLogin } from "../loginUtils";
import { trackLoginAttempt, trackLoginSuccess, trackRegisterForFree } from "../../feature/Analytics/ga/ga";
import { Button, LoadingButton } from "../../common/components/Button/Button";
import { REGISTER_PAGE } from "../../utils/url/staticUrls";
import { useTranslation } from "react-i18next";
import { Alert } from "~/common/m3/Alert/Alert";
import { TextField } from "~/common/m3/TextField/TextField";
import { Box, Stack } from "@mui/material";
import { useNumericScreenSize } from "~/common/hooks/useNumericScreenSize";

interface Props {
  initialEmail: string;
  onResetClick: (email: string) => void;
  // use to configure an alternative eventAction in GA
  alternativeGAAction?: string;
  alternativeLoginAction?: () => void;
  close: () => void;
}

const LoginPopup: FC<Props> = ({ close, initialEmail, onResetClick, alternativeGAAction, alternativeLoginAction }) => {
  const { t } = useTranslation();

  const { login, error, errorComponent, inFlight } = useLoginApi({
    onBeforeLogin: () => trackLoginAttempt(alternativeGAAction),
    onLoginSuccess: () => trackLoginSuccess(alternativeGAAction),
    alternativeLoginAction,
  });

  const emailRef = useRef<HTMLInputElement>(null);
  const passwdRef = useRef<HTMLInputElement>(null);

  const invalidInputs = error === "login.error.credentials";

  const forceLogin = isForcedLogin();

  const screenSize = useNumericScreenSize();
  const isMobile = screenSize <= useNumericScreenSize.phone;

  const resetCb = () => {
    let email = "";
    if (emailRef.current) {
      email = emailRef.current.value;
    }
    onResetClick(email);
  };

  const doLogin = async () => {
    if (!emailRef.current || !passwdRef.current) {
      return;
    }
    await login(emailRef.current.value, passwdRef.current.value);
  };

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    // noinspection JSIgnoredPromiseFromCall
    doLogin();
  };

  const resetPassword = (
    <div className={`d-flex ${isMobile ? "flex-justify-center" : "flex-justify-between"} flex-align-center mt-2 mb-3`}>
      <Link onClick={resetCb} data-test-id={"forgot-pw-link"}>
        {t("page.login.forgottenPwdLink")}
      </Link>
    </div>
  );

  const footer = (
    <div className={`d-flex flex-justify-between w-100 ${isMobile ? "flex-direction-column flex-align-center" : ""}`}>
      {!forceLogin && !isMobile && (
        <Button color={"primary"} variant={"text"} data-test-id={"login-cancel-button"} onClick={close}>
          {t("cancel")}
        </Button>
      )}
      {forceLogin && <div />}

      <Stack gap={2} direction={isMobile ? "column-reverse" : "row"} className={isMobile ? "w-100" : ""}>
        {isMobile && resetPassword}

        {!forceLogin && (
          <Button
            color={"secondary"}
            data-test-id={"login-register-for-free-button"}
            href={REGISTER_PAGE}
            onClick={() => trackRegisterForFree(alternativeGAAction ?? "Sign In")}
            fullWidth={isMobile}
          >
            {t("header.link.register")}
          </Button>
        )}
        <LoadingButton
          color={"primary"}
          inFlight={inFlight}
          data-test-id={"login-signin-button"}
          onClick={doLogin}
          fullWidth={isMobile}
        >
          {t("header.link.signIn")}
        </LoadingButton>
      </Stack>
    </div>
  );

  return (
    <Modal
      size={ModalSize.SMALL}
      onClose={close}
      footerChildren={footer}
      hideClose={forceLogin}
      title={t("header.link.signIn")}
    >
      <form onSubmit={onSubmit}>
        <button hidden type={"submit"} />
        <Stack gap={3} className="pt-1">
          <TextField
            inputRef={emailRef}
            defaultValue={initialEmail}
            name={"j_username"}
            data-test-id={"login-input"}
            label={t("page.login.email")}
            error={invalidInputs}
            fullWidth
          />

          <TextField
            inputRef={passwdRef}
            data-test-id={"password-input"}
            name={"j_password"}
            label={t("page.login.password")}
            type={"password"}
            error={invalidInputs}
            fullWidth
          />

          {error && <Box className={"mt-2"}>{errorComponent}</Box>}

          {!isMobile && resetPassword}
        </Stack>
      </form>
    </Modal>
  );
};
export default LoginPopup;

const getTimezoneString = () => {
  const offsetInMinutes = new Date().getTimezoneOffset() * -1;
  return offsetInMinutes.toString();
};

interface LoginOptions {
  onBeforeLogin?: () => void;
  onLoginSuccess?: () => void;
  alternativeLoginAction?: () => void;
}
export function useLoginApi(options?: LoginOptions) {
  const [error, setError] = useState("");
  const [inFlight, setInFlight] = useState(false);
  const { t } = useTranslation();

  const onBeforeLogin = options?.onBeforeLogin ?? trackLoginAttempt;
  const onLoginSuccess = options?.onLoginSuccess ?? trackLoginSuccess;
  const alternativeLoginAction = options?.alternativeLoginAction;

  const doLogin = async (login: string, password: string) => {
    onBeforeLogin();

    try {
      setInFlight(true);
      setError("");
      const result = await sendLoginRequest({
        login,
        password,
        timezone: getTimezoneString(),
      });
      if (!result.error) {
        onLoginSuccess();
        if (alternativeLoginAction) {
          alternativeLoginAction();
        } else {
          doOnLoginAction(result.redirectUrl);
        }
        return true;
      } else {
        setInFlight(false);
        setError(t(result.error));
        return false;
      }
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
      }
      setInFlight(false);
      return false;
    }
  };

  let errorComponent = undefined;
  if (error) {
    errorComponent = <Alert severity="error">{error}</Alert>;
  }

  return {
    login: doLogin,
    error,
    inFlight,
    errorComponent,
  };
}
